import React, { useEffect, useState } from "react";
import { Table, Tag, Input, Drawer, Popconfirm, message, Card, Row, Col, Timeline, Spin, Select, Button, Divider, Avatar, Tooltip, Modal } from "antd";
import { SearchOutlined, InfoCircleOutlined, DeleteOutlined, SaveOutlined, MailOutlined, PhoneOutlined, LinkedinOutlined, EnvironmentOutlined, BankOutlined } from "@ant-design/icons";
import { axiosInstant } from "src/api/api";
import { fetchCandidatePool } from 'src/api/api';
import { useUser } from "src/context/auth.context";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

const getFileUrl = (url?: string) => {
  if (!url) return undefined;
  return url.startsWith('http') ? url : `${API_BASE_URL}${url}`;
};

const activityIcon: Record<string, string> = {
  added_to_pool: '🟢',
  applied_to_job: '📝',
  job_match: '🤖',
  profile_updated: '✏️',
};

const healthCardStyle: React.CSSProperties = {
  borderRadius: 12,
  textAlign: 'center',
  boxShadow: '0 2px 8px rgba(0,0,0,0.07)',
};

interface PoolHealth {
  total: number;
  activeSeekers: number;
  addedLast30Days: number;
  passiveSeekers: number;
}

const CandidatePool: React.FC = () => {
  const [candidates, setCandidates] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCandidate, setSelectedCandidate] = useState<any | null>(null);
  const [drawerVisible, setDrawerVisible] = useState(false);
  const [health, setHealth] = useState<PoolHealth | null>(null);
  const [timeline, setTimeline] = useState<any[]>([]);
  const [timelineLoading, setTimelineLoading] = useState(false);
  const [note, setNote] = useState('');
  const [noteSaving, setNoteSaving] = useState(false);
  const [tags, setTags] = useState<string[]>([]);
  const [tagsSaving, setTagsSaving] = useState(false);
  const [autoSummary, setAutoSummary] = useState<any>(null);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [resumeModalOpen, setResumeModalOpen] = useState(false);

  const { user } = useUser();
  const recruiterId = user._id;

  useEffect(() => {
    if (!recruiterId) return;
    setLoading(true);
    fetchCandidatePool(recruiterId)
      .then(res => {
        const thirtyDaysAgo = new Date(Date.now() - 30 * 24 * 60 * 60 * 1000);
        const formattedCandidates = res.data.map((candidate: any) => ({
          ...candidate,
          name: candidate.profile?.firstName && candidate.profile?.lastName
            ? `${candidate.profile.firstName} ${candidate.profile.lastName}`
            : candidate.name || '-',
          position: candidate.profile?.positionTitle || candidate.position || '-',
          resume: getFileUrl(candidate.profile?.resume) || getFileUrl(candidate.resume),
          profilePhoto: getFileUrl(candidate.profile?.profilePhoto) || getFileUrl(candidate.profilePhoto),
          profile: candidate.profile || {},
        }));
        setCandidates(formattedCandidates);
        const total = formattedCandidates.length;
        const activeSeekers = formattedCandidates.filter((c: any) => c.profile?.isSearching ?? c.isSearching).length;
        const addedLast30Days = formattedCandidates.filter((c: any) => c.addedAt && new Date(c.addedAt) > thirtyDaysAgo).length;
        setHealth({ total, activeSeekers, addedLast30Days, passiveSeekers: total - activeSeekers });
      })
      .finally(() => setLoading(false));
  }, [recruiterId]);

  const openDrawer = (candidate: any) => {
    setSelectedCandidate(candidate);
    setDrawerVisible(true);
    setNote(candidate.recruiterNote || '');
    setTags(candidate.tags || []);
    setTimeline([]);
    setTimelineLoading(true);
    axiosInstant.get(`/jobs/candidate-pool/${candidate.id}/timeline`)
      .then(res => setTimeline(res.data || []))
      .catch(() => setTimeline([]))
      .finally(() => setTimelineLoading(false));

    setAutoSummary(null);
    setSummaryLoading(true);
    axiosInstant.get(`/jobs/candidate-pool/${candidate.id}/auto-summary`)
      .then(res => setAutoSummary(res.data))
      .catch(() => setAutoSummary(null))
      .finally(() => setSummaryLoading(false));
  };

  const saveNote = async () => {
    if (!selectedCandidate) return;
    setNoteSaving(true);
    try {
      await axiosInstant.patch(`/jobs/candidate-pool/${selectedCandidate.id}/note`, { note });
      setCandidates(prev => prev.map(c => c.id === selectedCandidate.id ? { ...c, recruiterNote: note } : c));
      setSelectedCandidate((prev: any) => ({ ...prev, recruiterNote: note }));
      message.success('Note saved.');
    } catch { message.error('Failed to save note.'); }
    finally { setNoteSaving(false); }
  };

  const saveTags = async (newTags: string[]) => {
    if (!selectedCandidate) return;
    setTags(newTags);
    setTagsSaving(true);
    try {
      await axiosInstant.patch(`/jobs/candidate-pool/${selectedCandidate.id}/tags`, { tags: newTags });
      setCandidates(prev => prev.map(c => c.id === selectedCandidate.id ? { ...c, tags: newTags } : c));
      setSelectedCandidate((prev: any) => ({ ...prev, tags: newTags }));
    } catch { message.error('Failed to save tags.'); }
    finally { setTagsSaving(false); }
  };

  const handleDeleteCandidate = async (record: any) => {
    try {
      await axiosInstant.delete(
        `/jobs/candidate-pool/${record.id}?recruiterId=${user._id}`
      );
      message.success("Candidate removed from pool.");
      setCandidates((prev) => prev.filter((c) => c.id !== record.id));
    } catch (err) {
      message.error("Failed to remove candidate.");
    }
  };

  const filteredCandidates = search.trim()
    ? candidates.filter(c => {
        const name = `${c.profile?.firstName || ''} ${c.profile?.lastName || ''}`.toLowerCase();
        return name.includes(search.toLowerCase()) || (c.email || '').toLowerCase().includes(search.toLowerCase());
      })
    : candidates;

  const columns = [
    {
      title: "First Name",
      key: "firstName",
      align: "center" as const,
      render: (_: any, record: any) => record.profile?.firstName || "-"
    },
    {
      title: "Last Name",
      key: "lastName",
      align: "center" as const,
      render: (_: any, record: any) => record.profile?.lastName || "-"
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "center" as const
    },
    {
      title: "Position Title",
      key: "positionTitle",
      align: "center" as const,
      render: (_: any, record: any) => record.profile?.positionTitle || "-"
    },
    { title: "Location", dataIndex: "location", key: "location", align: "center" as const },
    { title: "Years of Experience", dataIndex: "yearsOfExperience", key: "yearsOfExperience", align: "center" as const },
    {
      title: "Status",
      key: "status",
      align: "center" as const,
      render: (_: any, record: any) =>
        (record.profile?.isSearching ?? record.isSearching)
          ? <Tag color="green" style={{ fontSize: 14 }}>Active</Tag>
          : <Tag color="default" style={{ fontSize: 14 }}>Passive</Tag>,
    },
    {
      title: "More Info",
      key: "moreInfo",
      align: "center" as const,
      render: (_: any, record: any) => (
        <InfoCircleOutlined
          style={{ fontSize: 20, color: "#1890ff", cursor: "pointer" }}
          onClick={() => openDrawer(record)}
        />
      ),
    },
    {
      title: "Delete",
      key: "delete",
      align: "center" as const,
      render: (_: any, record: any) => (
        <Popconfirm
          title="Are you sure you want to remove this candidate from the pool?"
          onConfirm={() => handleDeleteCandidate(record)}
          okText="Yes"
          cancelText="No"
        >
          <DeleteOutlined style={{ color: "red", cursor: "pointer" }} />
        </Popconfirm>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ fontSize: 32, marginBottom: 24, fontWeight: 700 }}>Candidate Pool</h2>

      {/* Pool Health Overview */}
      <Row gutter={16} style={{ marginBottom: 28 }}>
        {[
          {
            label: 'Total Candidates',
            value: health?.total ?? '—',
            color: '#1890ff',
            icon: '👥',
            loading: loading,
          },
          {
            label: 'Active Job Seekers',
            value: health?.activeSeekers ?? '—',
            color: '#52c41a',
            icon: '🔍',
            loading: loading,
          },
          {
            label: 'Added Last 30 Days',
            value: health?.addedLast30Days ?? '—',
            color: '#722ed1',
            icon: '📅',
            loading: loading,
          },
          {
            label: 'Not Actively Seeking',
            value: health?.passiveSeekers ?? '—',
            color: '#fa8c16',
            icon: '⏸',
            loading: loading,
          },
        ].map(card => (
          <Col span={6} key={card.label}>
            <Card style={healthCardStyle} styles={{ body: { padding: '20px 16px' } }}>
              {card.loading ? (
                <Spin />
              ) : (
                <>
                  <div style={{ fontSize: 28, marginBottom: 4 }}>{card.icon}</div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: card.color, lineHeight: 1.1 }}>
                    {card.value}
                  </div>
                  <div style={{ fontSize: 16, color: '#595959', marginTop: 6 }}>{card.label}</div>
                </>
              )}
            </Card>
          </Col>
        ))}
      </Row>

      <Input
        placeholder="Search by name or email"
        prefix={<SearchOutlined />}
        style={{ width: 300, marginBottom: 16, fontSize: 16 }}
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        allowClear
      />
      <Table
        columns={columns.map(col => ({
          ...col,
          title: <span style={{ fontSize: 18, fontWeight: 700 }}>{col.title}</span>,
          render: col.render
            ? (text: any, record: any) => {
                const rendered = col.render(text, record);
                if (React.isValidElement(rendered)) return rendered;
                return <span style={{ fontSize: 16 }}>{String(rendered)}</span>;
              }
            : (text: any) => <span style={{ fontSize: 16 }}>{text}</span>,
        }))}
        dataSource={filteredCandidates}
        rowKey="id"
        loading={loading}
        bordered
        size="middle"
        pagination={{ pageSize: 10, style: { fontSize: 16 } }}
        components={{
          body: {
            cell: (props: any) => (
              <td {...props} style={{ ...props.style, fontSize: 16 }} />
            ),
          },
        }}
      />

      <Drawer
        title={null}
        open={drawerVisible}
        onClose={() => setDrawerVisible(false)}
        width={600}
        styles={{ body: { padding: 0 } }}
      >
        {selectedCandidate && (() => {
          const p = selectedCandidate.profile || {};
          const name = p.firstName ? `${p.firstName} ${p.lastName}` : selectedCandidate.name || '-';
          const initials = p.firstName ? `${p.firstName[0]}${p.lastName?.[0] || ''}`.toUpperCase() : '?';
          const isActive = p.isSearching ?? selectedCandidate.isSearching;
          return (
            <div style={{ fontSize: 15 }}>

              {/* ── Hero header ── */}
              <div style={{ background: 'linear-gradient(135deg, #1d3557 0%, #457b9d 100%)', padding: '24px 24px 20px', color: '#fff' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                  <Avatar
                    size={72}
                    src={selectedCandidate.profilePhoto || undefined}
                    style={{ background: '#a8dadc', color: '#1d3557', fontSize: 24, fontWeight: 700, flexShrink: 0 }}
                  >
                    {!selectedCandidate.profilePhoto && initials}
                  </Avatar>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 20, fontWeight: 700, color: '#fff', lineHeight: 1.2 }}>{name}</div>
                    {p.positionTitle && <div style={{ fontSize: 14, color: '#a8dadc', marginTop: 3 }}>{p.positionTitle}</div>}
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 10 }}>
                      <Tag color={isActive ? 'success' : 'default'} style={{ margin: 0 }}>
                        {isActive ? '🟢 Actively job hunting' : '⚪ Open to opportunities'}
                      </Tag>
                      {p.location && <Tag color="blue" style={{ margin: 0 }}>📍 Based in {p.location}</Tag>}
                      {p.yearsOfExperience != null && <Tag color="geekblue" style={{ margin: 0 }}>🏅 {p.yearsOfExperience} yrs experience</Tag>}
                      {p.relocation && <Tag color="cyan" style={{ margin: 0 }}>🏠 Relocation: {p.relocation}</Tag>}
                      {p.jobPreference && <Tag color="purple" style={{ margin: 0 }}>💼 Prefers {p.jobPreference}</Tag>}
                    </div>
                  </div>
                </div>

                {/* Contact row */}
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginTop: 16, fontSize: 13, color: '#cce' }}>
                  {(p.email || selectedCandidate.email) && (
                    <span><MailOutlined style={{ marginRight: 5 }} />{p.email || selectedCandidate.email}</span>
                  )}
                  {p.phoneNumber && (
                    <span><PhoneOutlined style={{ marginRight: 5 }} />{p.phoneNumber}</span>
                  )}
                  {p.linkedin && (
                    <a href={p.linkedin} target="_blank" rel="noopener noreferrer" style={{ color: '#a8dadc' }}>
                      <LinkedinOutlined style={{ marginRight: 5 }} />LinkedIn
                    </a>
                  )}
                </div>
              </div>

              <div style={{ padding: '0 24px 24px' }}>

                {/* ── Recruiter workspace ── */}
                <Divider orientation="left" style={{ fontSize: 13, color: '#888', marginTop: 20 }}>RECRUITER WORKSPACE</Divider>
                <div style={{ marginBottom: 14 }}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>Tags <span style={{ fontWeight: 400 }}>(auto-saves)</span></div>
                  <Select
                    mode="tags"
                    style={{ width: '100%' }}
                    placeholder="Add tags — e.g. Hot, Pipeline, Follow-up"
                    value={tags}
                    onChange={saveTags}
                    loading={tagsSaving}
                    options={[
                      { value: 'hot', label: '🔥 Hot' },
                      { value: 'pipeline', label: '📋 Pipeline' },
                      { value: 'follow-up', label: '📞 Follow-up' },
                      { value: 'interviewed', label: '✅ Interviewed' },
                      { value: 'on-hold', label: '⏸ On Hold' },
                    ]}
                  />
                </div>
                <div>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>Private Note</div>
                  <Input.TextArea
                    rows={3}
                    placeholder="Write a private note only you can see..."
                    value={note}
                    onChange={e => setNote(e.target.value)}
                    style={{ fontSize: 14, resize: 'vertical' }}
                  />
                  <Button type="primary" icon={<SaveOutlined />} onClick={saveNote} loading={noteSaving} style={{ marginTop: 8 }} size="small">
                    Save Note
                  </Button>
                </div>

                {/* ── Quick Insights ── */}
                <Divider orientation="left" style={{ fontSize: 13, color: '#888', marginTop: 24 }}>QUICK INSIGHTS</Divider>
                {summaryLoading ? (
                  <div style={{ textAlign: 'center', padding: 12 }}><Spin size="small" /></div>
                ) : !autoSummary ? (
                  <p style={{ color: '#bbb', fontSize: 13 }}>No application history yet.</p>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    <div style={{ fontSize: 14 }}>
                      📂 Applied <b>{autoSummary.totalApplications}</b> time{autoSummary.totalApplications !== 1 ? 's' : ''} · Last: <b>{autoSummary.latestRole}</b>
                      {autoSummary.latestDate && <span style={{ color: '#aaa', marginLeft: 5, fontSize: 13 }}>({new Date(autoSummary.latestDate).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })})</span>}
                    </div>
                    <div style={{ fontSize: 14 }}>
                      📌 Outcome:{' '}
                      <Tag color={autoSummary.latestStatus === 'hired' ? 'green' : ['rejected','auto_rejected'].includes(autoSummary.latestStatus) ? 'red' : autoSummary.latestStatus === 'offer_placed' ? 'gold' : 'blue'}>
                        {autoSummary.latestStatusLabel}
                      </Tag>
                    </div>
                    {autoSummary.bestScore && (
                      <div style={{ fontSize: 14 }}>
                        🤖 Best AI score: <b style={{ color: autoSummary.bestScore.score >= 70 ? '#52c41a' : autoSummary.bestScore.score >= 50 ? '#fa8c16' : '#f5222d' }}>{autoSummary.bestScore.score}%</b>
                        <span style={{ color: '#888', fontSize: 13, marginLeft: 5 }}>— {autoSummary.bestScore.role}</span>
                      </div>
                    )}
                    {autoSummary.latestFeedback && (
                      <div style={{ fontSize: 14 }}>
                        📋 Recruiter note{autoSummary.latestFeedbackRole ? ` on ${autoSummary.latestFeedbackRole}` : ''}:{' '}
                        <i style={{ color: '#555' }}>"{autoSummary.latestFeedback}"</i>
                      </div>
                    )}
                    {autoSummary.conversationCount > 0 && (
                      <div style={{ fontSize: 14 }}>💬 <b>{autoSummary.conversationCount}</b> message{autoSummary.conversationCount !== 1 ? 's' : ''} exchanged</div>
                    )}
                  </div>
                )}

                {/* ── Activity ── */}
                <Divider orientation="left" style={{ fontSize: 13, color: '#888', marginTop: 24 }}>ACTIVITY</Divider>
                {timelineLoading ? (
                  <div style={{ textAlign: 'center', padding: 16 }}><Spin /></div>
                ) : timeline.length === 0 ? (
                  <p style={{ color: '#bbb', fontSize: 13 }}>No activity recorded yet.</p>
                ) : (
                  <Timeline
                    items={timeline.map(event => ({
                      dot: <span style={{ fontSize: 14 }}>{activityIcon[event.type] || '📌'}</span>,
                      children: (
                        <div>
                          <div style={{ fontSize: 14 }}>{event.description}</div>
                          <div style={{ fontSize: 12, color: '#aaa', marginTop: 2 }}>
                            {new Date(event.createdAt).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                        </div>
                      ),
                    }))}
                  />
                )}

                {/* ── Resume ── */}
                {selectedCandidate.resume && (
                  <>
                    <Divider orientation="left" style={{ fontSize: 13, color: '#888', marginTop: 24 }}>RESUME</Divider>
                    <div style={{ border: '1px solid #d9d9d9', borderRadius: 8, overflow: 'hidden' }}>
                      <div style={{ background: '#323639', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '6px 12px' }}>
                        <span style={{ color: '#ccc', fontSize: 12 }}>📄 Resume Preview</span>
                        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                          <a href={selectedCandidate.resume} target="_blank" rel="noopener noreferrer" style={{ color: '#aaa', fontSize: 12 }}>
                            Open in new tab ↗
                          </a>
                          <Button
                            size="small"
                            onClick={() => setResumeModalOpen(true)}
                            style={{ background: '#555', border: 'none', color: '#fff', fontSize: 12 }}
                          >
                            ⛶ Expand
                          </Button>
                        </div>
                      </div>
                      <iframe
                        src={selectedCandidate.resume}
                        title="Resume"
                        width="100%"
                        height="420px"
                        style={{ display: 'block', border: 'none' }}
                      />
                    </div>
                  </>
                )}

              </div>
            </div>
          );
        })()}
      </Drawer>

      {/* Resume expanded modal */}
      <Modal
        open={resumeModalOpen}
        onCancel={() => setResumeModalOpen(false)}
        footer={null}
        width="80vw"
        style={{ top: 20 }}
        styles={{ body: { padding: 0, height: '85vh' } }}
        title={
          <span style={{ fontSize: 16 }}>
            📄 Resume — {selectedCandidate
              ? `${selectedCandidate.profile?.firstName || ''} ${selectedCandidate.profile?.lastName || ''}`.trim() || selectedCandidate.name || 'Candidate'
              : ''}
          </span>
        }
      >
        {selectedCandidate?.resume && (
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <iframe
              src={selectedCandidate.resume}
              title="Resume"
              width="100%"
              style={{ flex: 1, border: 'none', minHeight: 'calc(85vh - 55px)' }}
            />
            <div style={{ padding: '10px 16px', borderTop: '1px solid #f0f0f0', textAlign: 'right' }}>
              <a href={selectedCandidate.resume} target="_blank" rel="noopener noreferrer" style={{ fontSize: 13 }}>
                Open in new tab ↗
              </a>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CandidatePool;
